// EnvToggle — switches the console between deployments.
//
// Reloads the page rather than swapping the client live. convex.ts
// builds ConvexReactClient at module import, before React mounts;
// swapping backends in place would mean tearing down the provider,
// cancelling in-flight subscriptions, and reconciling React Query cache
// across two backends that share no IDs. A reload gets that for free.
// Switching environments is a rare, deliberate act.
//
// A styled label + dropdown (not a native <select>), and switching to
// production routes through our own confirm modal rather than the
// browser's window.confirm.
import { useEffect, useRef, useState } from 'react';
import { ENVIRONMENTS, getActiveEnv, setActiveEnv, type AdminEnv } from '@/lib/environments';
import { PlainConfirmModal } from './Modals';

// Status-dot colours: prod reuses the banner crimson; dev a calm sage.
const DOT: Record<AdminEnv, string> = { prod: '#B00020', dev: '#3F7D5A' };

export function EnvToggle() {
  const active = getActiveEnv();
  const [open, setOpen] = useState(false);
  const [confirmProd, setConfirmProd] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close the menu on outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function commit(env: AdminEnv) {
    setActiveEnv(env);
    window.location.reload();
  }

  function choose(env: AdminEnv) {
    setOpen(false);
    if (env === active) return;
    // Switching TO production is the one move worth a speed bump.
    if (env === 'prod') { setConfirmProd(true); return; }
    commit(env);
  }

  const isProd = active === 'prod';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={labelStyle}>Environment</div>

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={triggerStyle(isProd)}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
          <span style={dotStyle(active)} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {ENVIRONMENTS[active].label}
          </span>
        </span>
        <Chevron open={open} />
      </button>

      {open && (
        <div role="listbox" style={menuStyle}>
          {(Object.keys(ENVIRONMENTS) as AdminEnv[]).map((env) => {
            const selected = env === active;
            return (
              <button
                key={env}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => choose(env)}
                style={optionStyle(selected)}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-2)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = selected ? 'var(--surface-2)' : 'transparent'; }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}>
                  <span style={dotStyle(env)} />
                  {ENVIRONMENTS[env].label}
                </span>
                {selected && <Check />}
              </button>
            );
          })}
        </div>
      )}

      <PlainConfirmModal
        open={confirmProd}
        title="Switch to Production?"
        body="You're about to point the console at the production deployment. Actions here affect real users and real data."
        confirmLabel="Switch to production"
        confirmStyle="danger"
        onCancel={() => setConfirmProd(false)}
        onConfirm={() => commit('prod')}
      />
    </div>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width={14} height={14} viewBox="0 0 24 24" fill="none" aria-hidden="true"
      style={{ flexShrink: 0, transition: 'transform .15s ease', transform: open ? 'rotate(180deg)' : 'none' }}
    >
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Check() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, color: 'var(--coral)' }}>
      <path d="M5 12l5 5L19 7" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ───── styles ─────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  letterSpacing: 1,
  textTransform: 'uppercase',
  color: 'var(--ink-3)',
  marginBottom: 7,
};

function triggerStyle(isProd: boolean): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    width: '100%',
    padding: '11px 13px',
    borderRadius: 11,
    border: '1px solid',
    borderColor: isProd ? 'rgba(176,0,32,0.45)' : 'var(--line-strong)',
    background: isProd ? 'rgba(176,0,32,0.05)' : 'var(--surface)',
    color: isProd ? '#B00020' : 'var(--ink)',
    fontFamily: 'var(--font-ui)',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    lineHeight: 1.2,
  };
}

function dotStyle(env: AdminEnv): React.CSSProperties {
  return {
    width: 9,
    height: 9,
    borderRadius: '50%',
    background: DOT[env],
    flexShrink: 0,
    boxShadow: `0 0 0 3px ${env === 'prod' ? 'rgba(176,0,32,0.14)' : 'rgba(63,125,90,0.16)'}`,
  };
}

const menuStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  left: 0,
  right: 0,
  zIndex: 50,
  background: 'var(--surface)',
  border: '1px solid var(--line-strong)',
  borderRadius: 11,
  padding: 5,
  boxShadow: '0 16px 40px rgba(43,29,20,0.18)',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

function optionStyle(selected: boolean): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    width: '100%',
    padding: '9px 10px',
    borderRadius: 8,
    border: 'none',
    background: selected ? 'var(--surface-2)' : 'transparent',
    color: 'var(--ink)',
    fontFamily: 'var(--font-ui)',
    fontSize: 14.5,
    fontWeight: selected ? 600 : 500,
    cursor: 'pointer',
    textAlign: 'left',
  };
}
